import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { transactionAPI, paymentAPI } from '../services/api';
import { useAuth } from '../contexts/FirebaseAuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ShippingLabel from '../components/ShippingLabel';
import ShippingRateSelector from '../components/ShippingRateSelector';
import StarRating from '../components/common/StarRating';
import InfoTooltip from '../components/common/InfoTooltip';
import axios from 'axios';
import {
  TruckIcon,
  CheckCircle,
  MapPin,
  User,
  Shield,
  Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const TransactionDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Shipping form state
  const [showShippingForm, setShowShippingForm] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');

  // Dispute form state
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');

  // Escrow reject form state
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Rating form state
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState('');

  // Shipping label purchase state
  const [selectedShippingRate, setSelectedShippingRate] = useState(null);
  const [purchasingLabel, setPurchasingLabel] = useState(false);
  const [generatingLabel, setGeneratingLabel] = useState(false);
  const [claimingPayout, setClaimingPayout] = useState(false);

  useEffect(() => {
    fetchTransaction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchTransaction = async () => {
    try {
      setLoading(true);
      const response = await transactionAPI.getTransaction(id);
      setTransaction(response.data.data);
    } catch (err) {
      setError('Failed to load transaction');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkShipped = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!window.confirm('Mark this item as shipped? The buyer will be notified with the tracking info from the prepaid label.')) {
      return;
    }

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      // Route to Escrow.com only for genuine escrow transactions (those have an
      // escrowTransactionId). Stripe orders use the internal mark-shipped, which
      // uses the tracking/carrier already saved from the auto-purchased label.
      if (transaction.escrowTransactionId) {
        await paymentAPI.markEscrowShipped(id, {
          trackingNumber: trackingNumber || transaction.trackingNumber,
          carrier: carrier || transaction.carrier,
        });
      } else {
        await paymentAPI.markShipped(id, {
          // optional manual override; normally blank so the label's data is kept
          trackingNumber: trackingNumber || undefined,
          carrier: carrier || undefined,
        });
      }
      setSuccess('Item marked as shipped! The buyer has been notified.');
      setShowShippingForm(false);
      fetchTransaction();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to mark as shipped');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmDelivery = async () => {
    if (!window.confirm('Confirm that you received the item and are satisfied? This will release payment to the seller.')) {
      return;
    }

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      await paymentAPI.confirmDelivery(id);
      setSuccess('Delivery confirmed! Payment released to seller.');
      fetchTransaction();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to confirm delivery');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClaimPayout = async () => {
    setClaimingPayout(true);
    setError('');
    setSuccess('');
    try {
      const res = await transactionAPI.retryPayout(id);
      setSuccess(res.data?.message || 'Payout sent to your Stripe account!');
      fetchTransaction();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send payout');
    } finally {
      setClaimingPayout(false);
    }
  };

  const handleOpenDispute = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      await paymentAPI.openDispute(id, { reason: disputeReason });
      setSuccess('Dispute opened. Our team will review it shortly.');
      setShowDisputeForm(false);
      fetchTransaction();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to open dispute');
    } finally {
      setActionLoading(false);
    }
  };

  // Escrow-specific handlers
  const handleAcceptEscrowItem = async () => {
    if (!window.confirm('Are you satisfied with the item? This will release payment to the seller through Escrow.com.')) {
      return;
    }

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      await paymentAPI.acceptEscrowItem(id);
      setSuccess('Item accepted! Payment released to seller through Escrow.com.');
      fetchTransaction();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept item');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectEscrowItem = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      await paymentAPI.rejectEscrowItem(id, { reason: rejectReason });
      setSuccess('Item rejected. Escrow.com will assist with dispute resolution.');
      setShowRejectForm(false);
      fetchTransaction();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject item');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitRating = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      await paymentAPI.submitRating(id, { rating, review });
      setSuccess('Rating submitted! Thank you for your feedback.');
      setShowRatingForm(false);
      fetchTransaction();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit rating');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePurchaseLabel = async () => {
    if (!selectedShippingRate) {
      setError('Please select a shipping option');
      return;
    }

    setPurchasingLabel(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/shipping/purchase-label/${id}`,
        {
          rateId: selectedShippingRate.objectId,
          carrier: selectedShippingRate.provider,
          servicelevel: selectedShippingRate.servicelevel,
          amount: selectedShippingRate.amount,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setSuccess('Shipping label purchased successfully! The seller can now print it and ship your item.');
        fetchTransaction();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to purchase shipping label');
    } finally {
      setPurchasingLabel(false);
    }
  };

  const handleGenerateLabel = async () => {
    if (!window.confirm('Generate a prepaid shipping label for this order now? This charges the platform Shippo account.')) {
      return;
    }

    setGeneratingLabel(true);
    setError('');
    setSuccess('');

    try {
      const response = await transactionAPI.retryShippingLabel(id);
      if (response.data.success) {
        const { trackingNumber } = response.data.data || {};
        setSuccess(`Shipping label generated!${trackingNumber ? ` Tracking: ${trackingNumber}` : ''} You can now download it below.`);
        fetchTransaction();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate shipping label');
    } finally {
      setGeneratingLabel(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDaysRemaining = () => {
    if (!transaction?.autoReleaseDate) return null;
    const now = new Date();
    const release = new Date(transaction.autoReleaseDate);
    const diff = Math.ceil((release - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // NOTE: the authenticated user object uses _id (matches the rest of the app).
  // Using user?.id here was always undefined, so isBuyer/isSeller were always
  // false — hiding the seller's shipping-label download and buyer actions.
  const currentUserId = user?._id || user?.id;
  const isBuyer = transaction?.buyer?._id === currentUserId;
  const isSeller = transaction?.seller?._id === currentUserId;
  // Only a genuine Escrow.com transaction has an escrowTransactionId. The
  // paymentMethod field defaulted to 'escrow' for older internal (Stripe)
  // transactions, so routing by it sent internal orders to the Escrow.com API
  // (causing 404s on accept/ship). escrowTransactionId is the reliable signal.
  const isEscrowTxn = !!transaction?.escrowTransactionId;
  const daysRemaining = getDaysRemaining();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!transaction) {
    return (
      <div className="container" style={{ paddingTop: '40px' }}>
        <div className="card">
          <h2>Transaction not found</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '40px', paddingBottom: '40px', maxWidth: '900px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Link to="/transactions" style={{ color: 'var(--primary)', textDecoration: 'none', marginBottom: '16px', display: 'inline-block' }}>
          ← Back to Transactions
        </Link>
        <h1 style={{ marginBottom: '8px' }}>Transaction Details</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{
            padding: '6px 16px',
            borderRadius: '20px',
            backgroundColor: 'var(--secondary)',
            fontSize: '14px',
            fontWeight: '600',
            textTransform: 'capitalize',
          }}>
            {transaction.status}
          </span>
          <span style={{ color: 'var(--textSecondary)', fontSize: '14px' }}>
            Order #{transaction._id.slice(-8).toUpperCase()}
          </span>
        </div>
      </div>

      {/* Alerts */}
      {success && <div className="success-message" style={{ marginBottom: '16px' }}>{success}</div>}
      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {/* Shipping Label (for sellers) */}
      {isSeller && <ShippingLabel transaction={transaction} />}

      {/* Seller payout — funds released but not yet transferred to the seller */}
      {isSeller &&
        !transaction.fundsHeldInEscrow &&
        transaction.status === 'completed' &&
        transaction.payoutStatus !== 'paid' &&
        !transaction.stripeTransferId && (
        <div className="card" style={{ marginBottom: '24px', borderLeft: '4px solid var(--success)' }}>
          <h3 style={{ marginBottom: '8px' }}>💰 Your payout is ready</h3>
          <p style={{ color: 'var(--textSecondary)', fontSize: '14px', marginBottom: '16px' }}>
            The buyer's payment has been released. Claim your payout of{' '}
            <strong>${(transaction.sellerPayout / 100).toFixed(2)}</strong> to your connected Stripe account.
            If you haven't connected Stripe yet, do that first in{' '}
            <Link to="/payment-settings" style={{ color: 'var(--primary)' }}>Payment Settings</Link>.
          </p>
          <button
            onClick={handleClaimPayout}
            disabled={claimingPayout}
            className="btn btn-primary"
            style={{ width: '100%' }}
          >
            {claimingPayout ? 'Sending payout…' : `Get Paid — $${(transaction.sellerPayout / 100).toFixed(2)}`}
          </button>
        </div>
      )}

      {/* Generate label (seller) — when this is a shipping order without a label yet */}
      {isSeller &&
        transaction.purchaseType === 'shipping' &&
        !transaction.shippingLabel?.labelUrl &&
        !['refunded', 'cancelled', 'completed'].includes(transaction.status) && (
        <div className="card" style={{ marginBottom: '24px', borderLeft: '4px solid var(--primary)' }}>
          <h3 style={{ marginBottom: '8px' }}>Shipping Label</h3>
          <p style={{ color: 'var(--textSecondary)', fontSize: '14px', marginBottom: '16px' }}>
            No prepaid label has been generated for this order yet. Click below to create one
            (using the buyer's address and this item's package dimensions). It will be charged to
            the platform Shippo account and emailed to you.
          </p>
          <button
            onClick={handleGenerateLabel}
            disabled={generatingLabel}
            className="btn btn-primary"
            style={{ width: '100%' }}
          >
            {generatingLabel ? 'Generating Label…' : 'Generate Shipping Label'}
          </button>
          <p style={{ color: 'var(--textSecondary)', fontSize: '12px', marginTop: '10px' }}>
            Requires your phone, email, and address (in Settings) and package dimensions on the item.
          </p>
        </div>
      )}

      {/* Escrow Notice */}
      {transaction.fundsHeldInEscrow && transaction.status !== 'completed' && (
        <div className="card" style={{ marginBottom: '24px', backgroundColor: isEscrowTxn ? '#f0f9ff' : '#FEF3C7', borderLeft: `4px solid ${isEscrowTxn ? 'var(--primary)' : 'var(--warning)'}` }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Shield size={20} style={{ color: isEscrowTxn ? 'var(--primary)' : 'var(--warning)', flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                {isEscrowTxn ? 'Payment Held by Escrow.com' : 'Payment Held in Escrow'}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--textSecondary)' }}>
                {isEscrowTxn ? (
                  isBuyer ? (
                    <>
                      Your payment is securely held by Escrow.com. The seller will receive it after you accept the item
                      or automatically after the 1-day inspection period.
                    </>
                  ) : (
                    <>
                      Payment is securely held by Escrow.com and will be released after the buyer accepts the item
                      or automatically after the 1-day inspection period.
                    </>
                  )
                ) : (
                  isBuyer ? (
                    <>
                      Your payment is secure. The seller will receive it after you confirm delivery
                      {daysRemaining !== null && ` or in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} if no issues are reported.`}
                    </>
                  ) : (
                    <>
                      Payment will be released after the buyer confirms delivery
                      {daysRemaining !== null && ` or automatically in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}.`}
                    </>
                  )
                )}
              </div>
              {isEscrowTxn && (
                <div style={{ fontSize: '12px', color: 'var(--textSecondary)', marginTop: '8px' }}>
                  Escrow Transaction ID: {transaction.escrowTransactionId}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Shipping Label Purchase (for buyers) */}
      {isBuyer &&
       transaction.purchaseType === 'shipping' &&
       transaction.status === 'paid' &&
       !transaction.shippingLabel?.labelUrl &&
       transaction.item?.packageDimensions?.weight && (
        <div className="card" style={{ marginBottom: '24px', borderLeft: '4px solid var(--primary)' }}>
          <h3 style={{ marginBottom: '16px' }}>Purchase Shipping Label</h3>
          <p style={{ color: 'var(--textSecondary)', marginBottom: '20px' }}>
            Select a shipping method below. The label will be sent to the seller so they can ship your item.
          </p>

          <ShippingRateSelector
            transactionId={id}
            onRateSelected={setSelectedShippingRate}
            onError={setError}
          />

          {selectedShippingRate && (
            <button
              onClick={handlePurchaseLabel}
              disabled={purchasingLabel}
              className="btn btn-primary"
              style={{ marginTop: '20px', width: '100%' }}
            >
              {purchasingLabel ? 'Purchasing...' : `Purchase Label - $${selectedShippingRate.amount.toFixed(2)}`}
            </button>
          )}
        </div>
      )}

      {/* Item Details */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ marginBottom: '16px' }}>Item</h3>
        <div style={{ display: 'flex', gap: '16px' }}>
          <img
            src={transaction.item?.images?.[0]?.thumbnail || transaction.item?.images?.[0]?.fullSize}
            alt={transaction.item?.title}
            style={{
              width: '120px',
              height: '120px',
              objectFit: 'cover',
              borderRadius: '8px',
            }}
          />
          <div style={{ flex: 1 }}>
            <Link
              to={`/items/${transaction.item?._id}`}
              style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', display: 'block', color: 'inherit', textDecoration: 'none' }}
            >
              {transaction.item?.title}
            </Link>
            <p style={{ color: 'var(--textSecondary)', marginBottom: '12px', fontSize: '14px' }}>
              {transaction.item?.description?.substring(0, 150)}...
            </p>
            <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--primary)' }}>
              ${(transaction.amount / 100).toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      {/* Parties */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: 'var(--textSecondary)' }}>
            <User size={16} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>Seller</span>
          </div>
          <Link to={`/profile/${transaction.seller?.username}`} style={{ color: 'inherit', textDecoration: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img
                src={transaction.seller?.profileImage || `https://ui-avatars.com/api/?name=${transaction.seller?.username}`}
                alt={transaction.seller?.username}
                style={{ width: '40px', height: '40px', borderRadius: '50%' }}
              />
              <div>
                <div style={{ fontWeight: '600' }}>{transaction.seller?.username}</div>
                {transaction.seller?.location?.city && (
                  <div style={{ fontSize: '12px', color: 'var(--textSecondary)' }}>
                    {transaction.seller.location.city}, {transaction.seller.location.state}
                  </div>
                )}
              </div>
            </div>
          </Link>
        </div>

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: 'var(--textSecondary)' }}>
            <User size={16} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>Buyer</span>
          </div>
          <Link to={`/profile/${transaction.buyer?.username}`} style={{ color: 'inherit', textDecoration: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img
                src={transaction.buyer?.profileImage || `https://ui-avatars.com/api/?name=${transaction.buyer?.username}`}
                alt={transaction.buyer?.username}
                style={{ width: '40px', height: '40px', borderRadius: '50%' }}
              />
              <div>
                <div style={{ fontWeight: '600' }}>{transaction.buyer?.username}</div>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* Purchase Details */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ marginBottom: '16px' }}>
          {isSeller && !isBuyer ? 'Payout Breakdown' : 'Payment Breakdown'}
        </h3>
        <div style={{ display: 'grid', gap: '12px' }}>
          {/* Item price is shown to everyone */}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'var(--textSecondary)' }}>Item Price</span>
            <span style={{ fontWeight: '500' }}>${(transaction.amount / 100).toFixed(2)}</span>
          </div>

          {/* SELLER view: commission + payout */}
          {isSeller && !isBuyer ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--textSecondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Seller Commission ({transaction.platformFee && transaction.amount
                    ? Math.round((transaction.platformFee / transaction.amount) * 100)
                    : ''}%)
                  <InfoTooltip text="BabyResell's commission on the sale price — this is how the marketplace earns. It's deducted from your payout, not charged separately." />
                </span>
                <span style={{ fontWeight: '500', color: 'var(--error)' }}>
                  -${(transaction.platformFee / 100).toFixed(2)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
                <span style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Your Payout
                  <InfoTooltip text="What you receive: the item price minus our commission. It's released to you after the buyer confirms delivery (or automatically after the inspection period)." />
                </span>
                <span style={{ fontWeight: '700', fontSize: '18px', color: 'var(--primary)' }}>
                  ${(transaction.sellerPayout / 100).toFixed(2)}
                </span>
              </div>
              <div style={{ fontSize: '13px', color: 'var(--textSecondary)' }}>
                Shipping is prepaid by the buyer at no cost to you.
              </div>
            </>
          ) : (
            /* BUYER (and admin) view: what the buyer paid */
            <>
              {transaction.shippingCost > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--textSecondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Shipping
                    <InfoTooltip text="The live carrier rate based on the seller's location and package size, plus a small handling buffer. Covers the prepaid shipping label." />
                  </span>
                  <span style={{ fontWeight: '500' }}>${(transaction.shippingCost / 100).toFixed(2)}</span>
                </div>
              )}
              {transaction.serviceFee > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--textSecondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Service Fee
                    <InfoTooltip text="This fee covers secure payment processing (card fees) and buyer protection — your payment is held safely until you confirm the item arrived as described. It helps keep BabyResell running." />
                  </span>
                  <span style={{ fontWeight: '500' }}>${(transaction.serviceFee / 100).toFixed(2)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
                <span style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Total Paid
                  <InfoTooltip text="The full amount charged to your card: item price + shipping + service fee." />
                </span>
                <span style={{ fontWeight: '700', fontSize: '18px', color: 'var(--primary)' }}>
                  ${((transaction.buyerTotal
                    || (transaction.amount + (transaction.shippingCost || 0) + (transaction.serviceFee || 0))) / 100).toFixed(2)}
                </span>
              </div>

              {/* Admins also see the seller-side numbers */}
              {!isBuyer && !isSeller && (
                <div style={{ marginTop: '8px', paddingTop: '12px', borderTop: '1px dashed var(--border)', display: 'grid', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--textSecondary)' }}>Seller Commission</span>
                    <span style={{ fontWeight: '500' }}>${(transaction.platformFee / 100).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--textSecondary)' }}>Seller Payout</span>
                    <span style={{ fontWeight: '500' }}>${(transaction.sellerPayout / 100).toFixed(2)}</span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Refund note */}
          {transaction.status === 'refunded' && transaction.refundAmount != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid var(--border)', color: 'var(--error)' }}>
              <span style={{ fontWeight: '600' }}>Refunded</span>
              <span style={{ fontWeight: '700' }}>${(transaction.refundAmount / 100).toFixed(2)}</span>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
            <span style={{ color: 'var(--textSecondary)' }}>Purchase Type</span>
            <span style={{ fontWeight: '500', textTransform: 'capitalize' }}>
              {transaction.purchaseType?.replace('-', ' ')}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'var(--textSecondary)' }}>Order Date</span>
            <span style={{ fontWeight: '500' }}>{formatDate(transaction.createdAt)}</span>
          </div>
        </div>
      </div>

      {/* Shipping Address (if applicable) */}
      {transaction.shippingAddress && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <MapPin size={18} />
            <h3>Shipping Address</h3>
          </div>
          <div style={{ color: 'var(--textSecondary)' }}>
            <div>{transaction.shippingAddress.fullName}</div>
            <div>{transaction.shippingAddress.street}</div>
            <div>
              {transaction.shippingAddress.city}, {transaction.shippingAddress.state} {transaction.shippingAddress.zipCode}
            </div>
            {transaction.shippingAddress.phone && <div>Phone: {transaction.shippingAddress.phone}</div>}
          </div>
        </div>
      )}

      {/* Tracking Info */}
      {transaction.trackingNumber && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <TruckIcon size={18} />
            <h3>Shipping Information</h3>
          </div>
          <div>
            <div style={{ marginBottom: '8px' }}>
              <span style={{ color: 'var(--textSecondary)' }}>Carrier: </span>
              <span style={{ fontWeight: '500' }}>{transaction.carrier}</span>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <span style={{ color: 'var(--textSecondary)' }}>Tracking Number: </span>
              <span style={{ fontWeight: '500' }}>{transaction.trackingNumber}</span>
            </div>
            {transaction.shippedAt && (
              <div>
                <span style={{ color: 'var(--textSecondary)' }}>Shipped: </span>
                <span style={{ fontWeight: '500' }}>{formatDate(transaction.shippedAt)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Seller Actions */}
      {isSeller && transaction.status === 'paid' && !showShippingForm && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px' }}>Seller Actions</h3>
          {transaction.trackingNumber ? (
            <p style={{ color: 'var(--textSecondary)', fontSize: '14px', marginBottom: '16px' }}>
              Tracking is ready{transaction.carrier ? ` (${transaction.carrier}` : ''}
              {transaction.trackingNumber ? `${transaction.carrier ? ' ' : '('}${transaction.trackingNumber})` : ''}.
              Drop off the package, then mark it shipped to notify the buyer.
            </p>
          ) : (
            <p style={{ color: 'var(--textSecondary)', fontSize: '14px', marginBottom: '16px' }}>
              You can mark this as shipped now. (No prepaid label tracking was found —
              you can optionally add a tracking number below.)
            </p>
          )}
          <button
            onClick={handleMarkShipped}
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={actionLoading}
          >
            {actionLoading ? 'Marking as Shipped…' : 'Mark as Shipped'}
          </button>
          {!transaction.trackingNumber && (
            <button
              onClick={() => setShowShippingForm(true)}
              className="btn"
              style={{ width: '100%', marginTop: '8px' }}
            >
              Add tracking manually (optional)
            </button>
          )}
        </div>
      )}

      {/* Optional manual tracking form (only used when there's no label tracking) */}
      {showShippingForm && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px' }}>Add Tracking (optional)</h3>
          <form onSubmit={handleMarkShipped}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Carrier
              </label>
              <input
                type="text"
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                className="input"
                placeholder="e.g., USPS, UPS, FedEx"
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Tracking Number
              </label>
              <input
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                className="input"
                placeholder="Enter tracking number"
              />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={actionLoading}
                style={{ flex: 1 }}
              >
                {actionLoading ? 'Submitting...' : 'Mark as Shipped'}
              </button>
              <button
                type="button"
                onClick={() => setShowShippingForm(false)}
                className="btn"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Buyer Actions */}
      {isBuyer && (transaction.status === 'shipped' || transaction.status === 'delivered') && !showDisputeForm && !showRejectForm && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px' }}>Buyer Actions</h3>
          {isEscrowTxn ? (
            <>
              <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px' }}>
                <div style={{ fontSize: '14px', color: 'var(--textSecondary)', marginBottom: '8px' }}>
                  <strong>Inspection Period:</strong> You have 1 day to inspect the item.
                </div>
                {transaction.escrowInspectionEndsAt && (
                  <div style={{ fontSize: '13px', color: 'var(--textSecondary)' }}>
                    Inspection ends: {formatDate(transaction.escrowInspectionEndsAt)}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={handleAcceptEscrowItem}
                  className="btn btn-primary"
                  disabled={actionLoading}
                  style={{ flex: 1 }}
                >
                  {actionLoading ? 'Processing...' : '✓ Accept Item & Release Payment'}
                </button>
                <button
                  onClick={() => setShowRejectForm(true)}
                  className="btn"
                  disabled={actionLoading}
                  style={{ flex: 1, borderColor: 'var(--error)', color: 'var(--error)' }}
                >
                  ✗ Reject Item
                </button>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleConfirmDelivery}
                className="btn btn-primary"
                disabled={actionLoading}
                style={{ flex: 1 }}
              >
                {actionLoading ? 'Processing...' : 'Confirm Delivery & Release Payment'}
              </button>
              <button
                onClick={() => setShowDisputeForm(true)}
                className="btn"
                style={{ flex: 1, borderColor: 'var(--error)', color: 'var(--error)' }}
              >
                Open Dispute
              </button>
            </div>
          )}
        </div>
      )}

      {/* Escrow Reject Form */}
      {showRejectForm && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px' }}>Reject Item</h3>
          <form onSubmit={handleRejectEscrowItem}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Reason for Rejection *
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="input"
                rows="4"
                placeholder="Describe why you're rejecting the item..."
                required
              />
              <div style={{ fontSize: '13px', color: 'var(--textSecondary)', marginTop: '8px' }}>
                Escrow.com will review your rejection and assist with resolution or refund.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                className="btn"
                disabled={actionLoading}
                style={{ flex: 1, backgroundColor: 'var(--error)', borderColor: 'var(--error)', color: 'white' }}
              >
                {actionLoading ? 'Submitting...' : 'Submit Rejection'}
              </button>
              <button
                type="button"
                onClick={() => setShowRejectForm(false)}
                className="btn"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Dispute Form */}
      {showDisputeForm && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px' }}>Open Dispute</h3>
          <form onSubmit={handleOpenDispute}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Reason for Dispute *
              </label>
              <textarea
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                className="input"
                rows="4"
                placeholder="Describe the issue..."
                required
              />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                className="btn"
                disabled={actionLoading}
                style={{ flex: 1, backgroundColor: 'var(--error)', borderColor: 'var(--error)', color: 'white' }}
              >
                {actionLoading ? 'Submitting...' : 'Submit Dispute'}
              </button>
              <button
                type="button"
                onClick={() => setShowDisputeForm(false)}
                className="btn"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Completed Transaction */}
      {transaction.status === 'completed' && (
        <div className="card" style={{ marginBottom: '24px', backgroundColor: 'var(--success)', color: 'white', borderColor: 'var(--success)' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <CheckCircle size={24} />
            <div>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>Transaction Completed</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>
                {transaction.fundsReleasedAt && `Payment released on ${formatDate(transaction.fundsReleasedAt)}`}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rating Section - Buyer Rating Seller */}
      {(transaction.status === 'completed' || transaction.status === 'delivered') && isBuyer && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px' }}>Rate the Seller</h3>

          {transaction.buyerRating ? (
            <div>
              <div style={{ marginBottom: '12px' }}>
                <StarRating rating={transaction.buyerRating} size={24} showNumber={false} />
              </div>
              {transaction.buyerReview && (
                <p style={{ color: 'var(--textSecondary)', marginTop: '12px' }}>
                  "{transaction.buyerReview}"
                </p>
              )}
              <p style={{ fontSize: '14px', color: 'var(--textSecondary)', marginTop: '8px' }}>
                Rated on {formatDate(transaction.buyerRatedAt)}
              </p>
            </div>
          ) : (
            <>
              {!showRatingForm ? (
                <button
                  onClick={() => setShowRatingForm(true)}
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                >
                  Rate Seller
                </button>
              ) : (
                <form onSubmit={handleSubmitRating}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Your Rating *
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          size={32}
                          style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                          fill={star <= (hoverRating || rating) ? '#FFD700' : 'none'}
                          stroke={star <= (hoverRating || rating) ? '#FFD700' : 'currentColor'}
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          onClick={() => setRating(star)}
                        />
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Review (Optional)
                    </label>
                    <textarea
                      value={review}
                      onChange={(e) => setReview(e.target.value)}
                      className="input"
                      rows="4"
                      placeholder="Share your experience with this seller..."
                      style={{ resize: 'vertical' }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={actionLoading || rating === 0}
                      style={{ flex: 1 }}
                    >
                      {actionLoading ? 'Submitting...' : 'Submit Rating'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowRatingForm(false);
                        setRating(0);
                        setReview('');
                      }}
                      className="btn"
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      )}

      {/* Rating Section - Seller Rating Buyer */}
      {(transaction.status === 'completed' || transaction.status === 'delivered') && isSeller && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px' }}>Rate the Buyer</h3>

          {transaction.sellerRating ? (
            <div>
              <div style={{ marginBottom: '12px' }}>
                <StarRating rating={transaction.sellerRating} size={24} showNumber={false} />
              </div>
              {transaction.sellerReview && (
                <p style={{ color: 'var(--textSecondary)', marginTop: '12px' }}>
                  "{transaction.sellerReview}"
                </p>
              )}
              <p style={{ fontSize: '14px', color: 'var(--textSecondary)', marginTop: '8px' }}>
                Rated on {formatDate(transaction.sellerRatedAt)}
              </p>
            </div>
          ) : (
            <>
              {!showRatingForm ? (
                <button
                  onClick={() => setShowRatingForm(true)}
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                >
                  Rate Buyer
                </button>
              ) : (
                <form onSubmit={handleSubmitRating}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Your Rating *
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          size={32}
                          style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                          fill={star <= (hoverRating || rating) ? '#FFD700' : 'none'}
                          stroke={star <= (hoverRating || rating) ? '#FFD700' : 'currentColor'}
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          onClick={() => setRating(star)}
                        />
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Review (Optional)
                    </label>
                    <textarea
                      value={review}
                      onChange={(e) => setReview(e.target.value)}
                      className="input"
                      rows="4"
                      placeholder="Share your experience with this buyer..."
                      style={{ resize: 'vertical' }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={actionLoading || rating === 0}
                      style={{ flex: 1 }}
                    >
                      {actionLoading ? 'Submitting...' : 'Submit Rating'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowRatingForm(false);
                        setRating(0);
                        setReview('');
                      }}
                      className="btn"
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionDetail;
